import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, Package, Settings, Users, Headphones, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { toast } from 'sonner';

const TORTLAB_LOGO = "https://customer-assets.emergentagent.com/job_35f2eb62-f2f6-4b48-9388-e2c1ee261a61/artifacts/tfwz2zen_generated-image-1%20%284%29.png";

export default function Pricing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/pricing-plans');
      setPlans(response.data.plans);
    } catch (error) {
      toast.error('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    // Check if user is logged in
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast.info('Please log in to subscribe to a plan');
      navigate('/login');
      return;
    }

    setSelectedPlan(planId);
    setProcessing(true);

    try {
      const response = await api.post('/tenant/subscription/change-plan', {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate checkout');
      setProcessing(false);
      setSelectedPlan(null);
    }
  };

  const getPlanIcon = (plan) => {
    if (plan.includes_ongoing_service && plan.hardware_type === 'Upgraded Tablet') {
      return <Sparkles className="w-6 h-6" />;
    }
    if (plan.includes_ongoing_service) {
      return <Headphones className="w-6 h-6" />;
    }
    if (plan.includes_initial_setup) {
      return <Users className="w-6 h-6" />;
    }
    return <Settings className="w-6 h-6" />;
  };

  const getPlanFeatures = (plan) => {
    const features = [];
    
    features.push('Unlimited AI chat sessions');
    features.push('Custom agent configuration');
    features.push('Quick action buttons');
    features.push('QR code generation');
    features.push('Google Business Profile integration');
    
    if (plan.includes_hardware) {
      features.push(`${plan.hardware_type} included`);
    } else {
      features.push('Use your own tablet');
    }
    
    if (plan.includes_initial_setup) {
      features.push('Professional setup & configuration');
    } else {
      features.push('Self-service setup');
    }
    
    if (plan.includes_ongoing_service) {
      features.push('Ongoing content optimization');
      features.push('Priority support');
    }
    
    if (plan.hardware_type === 'Upgraded Tablet') {
      features.push('Dedicated account manager');
      features.push('Custom integrations');
    }
    
    return features;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <img 
              src={TORTLAB_LOGO} 
              alt="TortLab" 
              className="h-16 mx-auto mb-4 object-contain"
              data-testid="pricing-logo"
            />
            <h1 className="text-4xl font-heading font-bold text-foreground" data-testid="pricing-title">
              Simple, Transparent Pricing
            </h1>
            <p className="text-lg text-muted-foreground mt-2">
              Choose the plan that fits your business needs
            </p>
          </div>
        </div>
      </header>

      {/* Pricing Cards */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => {
            const isPopular = plan.name.includes('Managed + Ongoing');
            const isPremium = plan.name.includes('Premium');
            
            return (
              <Card 
                key={plan.plan_id} 
                className={`relative flex flex-col border-2 transition-all hover:shadow-xl ${
                  isPopular ? 'border-primary shadow-lg scale-105' : 'border-border'
                } ${isPremium ? 'bg-gradient-to-br from-slate-900 to-slate-800 text-white' : ''}`}
                data-testid={`plan-card-${plan.plan_id}`}
              >
                {isPopular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white">
                    Most Popular
                  </Badge>
                )}
                
                <CardHeader className="pb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    isPremium ? 'bg-white/10' : 'bg-primary/10'
                  }`}>
                    <div className={isPremium ? 'text-white' : 'text-primary'}>
                      {getPlanIcon(plan)}
                    </div>
                  </div>
                  <CardTitle className={`text-xl font-heading ${isPremium ? 'text-white' : ''}`}>
                    {plan.name}
                  </CardTitle>
                  <CardDescription className={isPremium ? 'text-slate-300' : ''}>
                    {plan.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-1">
                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className={`text-4xl font-bold ${isPremium ? 'text-white' : 'text-foreground'}`}>
                        ${plan.monthly_price}
                      </span>
                      <span className={`ml-2 ${isPremium ? 'text-slate-300' : 'text-muted-foreground'}`}>
                        /month
                      </span>
                    </div>
                    {plan.setup_fee > 0 && (
                      <p className={`text-sm mt-1 ${isPremium ? 'text-slate-400' : 'text-muted-foreground'}`}>
                        + ${plan.setup_fee} one-time setup fee
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3">
                    {getPlanFeatures(plan).map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <Check className={`w-5 h-5 mr-2 flex-shrink-0 ${
                          isPremium ? 'text-green-400' : 'text-green-500'
                        }`} />
                        <span className={`text-sm ${isPremium ? 'text-slate-200' : 'text-muted-foreground'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter>
                  <Button
                    onClick={() => handleSelectPlan(plan.plan_id)}
                    disabled={processing}
                    className={`w-full rounded-full ${
                      isPremium 
                        ? 'bg-white text-slate-900 hover:bg-slate-100' 
                        : isPopular 
                          ? 'shadow-lg shadow-primary/20' 
                          : ''
                    }`}
                    variant={isPopular || isPremium ? 'default' : 'outline'}
                    data-testid={`select-plan-${plan.plan_id}`}
                  >
                    {processing && selectedPlan === plan.plan_id ? (
                      'Processing...'
                    ) : (
                      <>
                        Get Started
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* FAQ or Additional Info */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
            All Plans Include
          </h2>
          <div className="flex flex-wrap justify-center gap-4 text-muted-foreground">
            <Badge variant="secondary" className="text-sm py-2 px-4">
              <Check className="w-4 h-4 mr-2" />
              Unlimited AI Conversations
            </Badge>
            <Badge variant="secondary" className="text-sm py-2 px-4">
              <Check className="w-4 h-4 mr-2" />
              Voice Input Support
            </Badge>
            <Badge variant="secondary" className="text-sm py-2 px-4">
              <Check className="w-4 h-4 mr-2" />
              Custom Branding
            </Badge>
            <Badge variant="secondary" className="text-sm py-2 px-4">
              <Check className="w-4 h-4 mr-2" />
              Analytics Dashboard
            </Badge>
          </div>
        </div>

        {/* Back to Dashboard */}
        <div className="mt-12 text-center">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
            ← Back to Dashboard
          </Button>
        </div>
      </main>
    </div>
  );
}
